const DEFAULT_THROTTLE_MS = 30000;
export const CLIENT_TELEMETRY_SCHEMA_VERSION = "upload-v2-client-telemetry/1";
const MAX_BUFFERED_EVENTS = 250;
const MAX_WARN_ERROR_SAMPLES = 25;
const MAX_INFO_EVENT_SAMPLES = 20;
const MAX_FAILED_ENTRY_SAMPLES = 25;
const MAX_CLIENT_TELEMETRY_BYTES = 28 * 1024;
const throttleState = new Map();
const bufferedEvents = [];
const eventCounters = {};
const flushedTelemetryByJob = new Set();
const SENSITIVE_KEYS = new Set([
  "authorization",
  "bearer",
  "cookie",
  "cookies",
  "credential",
  "credentials",
  "file",
  "files",
  "manifest",
  "entries",
  "rawmanifest",
  "relativepath",
  "path",
  "name",
  "secret",
  "signature",
  "sourcehandle",
  "sourceroothandle",
  "signedurl",
  "signeduploadurl",
  "storageurl",
  "uploadurl",
  "url",
  "sessionuri",
  "token",
  "requestbody",
  "responsebody",
  "body",
  "googleaccessid",
  "googlesignature",
  "xgoogcredential",
  "xgoogsignature",
]);
const NOISY_UPLOAD_EVENTS = new Set([
  "entry-upload-progress",
  "upload-job-keep-alive-sent",
  "upload-job-keep-alive-start",
  "upload-job-keep-alive-stop",
  "local-lease-touch",
  "upload-job-details-hydration-complete",
  "my-uploads-local-summary-hydrated",
]);

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

export function uploadDiagnosticStart() {
  return nowMs();
}

export function uploadDiagnosticDurationMs(startedAt) {
  if (!Number.isFinite(startedAt)) {
    return null;
  }
  return Math.max(0, Math.round(nowMs() - startedAt));
}

function sanitizeString(value) {
  return String(value)
    .replace(/https?:\/\/\S+/gi, "[redacted-url]")
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [redacted]")
    .replace(/(authorization|password|token|secret|signature|credential|googleAccessId|x-goog-signature|x-goog-credential|uploadUrl|signedUrl|signedUploadUrl|storageUrl|sessionUri)=([^&\s]+)/gi, "$1=[redacted]");
}

function normalizeSensitiveKey(key) {
  return String(key || "").replace(/[^A-Za-z0-9]/g, "").toLowerCase();
}

export function sanitizeUploadDiagnosticContext(context = {}) {
  if (!context || typeof context !== "object") {
    return {};
  }
  return Object.keys(context).reduce((safe, key) => {
    if (SENSITIVE_KEYS.has(normalizeSensitiveKey(key))) {
      return safe;
    }
    const value = context[key];
    if (value === undefined || typeof value === "function") {
      return safe;
    }
    if (value instanceof Error) {
      safe[key] = sanitizeUploadError(value);
    } else if (Array.isArray(value)) {
      safe[key] = value.slice(0, 25).map((item) => (
        item && typeof item === "object" ? sanitizeUploadDiagnosticContext(item) : (
          typeof item === "string" ? sanitizeString(item) : item
        )
      ));
      safe[`${key}Truncated`] = value.length > 25;
    } else if (value && typeof value === "object") {
      safe[key] = sanitizeUploadDiagnosticContext(value);
    } else if (typeof value === "string") {
      safe[key] = sanitizeString(value);
    } else {
      safe[key] = value;
    }
    return safe;
  }, {});
}

export function sanitizeUploadError(error) {
  if (!error) {
    return null;
  }
  return {
    name: error.name,
    code: error.code,
    message: sanitizeString(error.message || String(error)),
  };
}

export function uploadDiagnosticLog(level, event, context = {}, error = null) {
  bufferUploadDiagnosticEvent(level, event, context, error);
  if (typeof console === "undefined") {
    return;
  }
  const logger = console[level] || console.log;
  const payload = {
    event,
    ...sanitizeUploadDiagnosticContext(context),
  };
  if (error) {
    payload.error = sanitizeUploadError(error);
  }
  logger.call(console, "[upload-v2]", payload);
}

function bufferUploadDiagnosticEvent(level, event, context = {}, error = null) {
  if (!event || NOISY_UPLOAD_EVENTS.has(event)) {
    return;
  }
  const normalizedLevel = String(level || "info").toLowerCase();
  eventCounters[event] = (eventCounters[event] || 0) + 1;
  if (normalizedLevel === "debug") {
    return;
  }
  bufferedEvents.push({
    at: new Date().toISOString(),
    level: normalizedLevel,
    event,
    context: sanitizeUploadDiagnosticContext(context),
    error: sanitizeUploadError(error),
  });
  if (bufferedEvents.length > MAX_BUFFERED_EVENTS) {
    bufferedEvents.splice(0, bufferedEvents.length - MAX_BUFFERED_EVENTS);
  }
}

function statusCounts(entries = []) {
  const counts = {
    PENDING: 0,
    IN_PROGRESS: 0,
    COMPLETE: 0,
    ERROR: 0,
    CANCELLED: 0,
    TIMED_OUT: 0,
    ABANDONED: 0,
  };
  entries.forEach((entry) => {
    const status = entry?.clientStatus || entry?.status || "PENDING";
    if (status === "QUEUED" || status === "CREATING_FILE" || status === "UPLOADING" || status === "FINALISING" || status === "CREATING_FOLDER") {
      counts.IN_PROGRESS += 1;
    } else if (status === "FAILED") {
      counts.ERROR += 1;
    } else if (counts[status] !== undefined) {
      counts[status] += 1;
    }
  });
  return counts;
}

function entryTypeCounts(entries = []) {
  return entries.reduce((counts, entry) => {
    if (entry?.entryType === "FILE") {
      counts.files += 1;
    } else if (entry?.entryType === "FOLDER") {
      counts.folders += 1;
    }
    counts.entries += 1;
    return counts;
  }, { entries: 0, files: 0, folders: 0 });
}

function failedEntrySamples(entries = []) {
  return entries
    .filter((entry) => ["ERROR", "FAILED", "CANCELLED", "TIMED_OUT", "ABANDONED"].includes(entry?.clientStatus || entry?.status))
    .slice(0, MAX_FAILED_ENTRY_SAMPLES)
    .map((entry) => sanitizeUploadDiagnosticContext({
      uploadJobLogId: entry.uploadJobLogId || entry.serverLogId || entry.id,
      clientEntryId: entry.clientEntryId,
      itemId: entry.itemId,
      sourceHandleId: entry.sourceHandleId,
      status: entry.clientStatus || entry.status,
      errorCode: entry.errorCode,
    }));
}

function summarizeBufferedEvents(uploadJobId) {
  const relevantEvents = bufferedEvents.filter((event) => (
    !uploadJobId || !event.context?.uploadJobId || String(event.context.uploadJobId) === String(uploadJobId)
  ));
  const warnError = relevantEvents
    .filter((event) => event.level === "warn" || event.level === "error")
    .slice(-MAX_WARN_ERROR_SAMPLES);
  const infoSamples = relevantEvents
    .filter((event) => event.level === "info")
    .slice(-MAX_INFO_EVENT_SAMPLES)
    .map((event) => ({
      at: event.at,
      event: event.event,
      context: event.context,
    }));
  return {
    counters: { ...eventCounters },
    warnError,
    infoSamples,
  };
}

function payloadBytes(payload) {
  return new Blob([JSON.stringify(payload)]).size;
}

function fitPayload(payload) {
  let fitted = payload;
  while (payloadBytes(fitted) > MAX_CLIENT_TELEMETRY_BYTES && fitted.diagnostics?.warnError?.length) {
    fitted = {
      ...fitted,
      diagnostics: {
        ...fitted.diagnostics,
        warnError: fitted.diagnostics.warnError.slice(1),
        omitted: {
          ...(fitted.diagnostics.omitted || {}),
          warnErrorSamples: true,
        },
      },
    };
  }
  if (payloadBytes(fitted) > MAX_CLIENT_TELEMETRY_BYTES) {
    fitted = {
      uploadJobId: payload.uploadJobId,
      terminalStatus: payload.terminalStatus,
      clientTelemetrySchemaVersion: payload.clientTelemetrySchemaVersion,
      generatedAt: payload.generatedAt,
      aggregateCounts: payload.aggregateCounts,
      localIndexedDb: payload.localIndexedDb,
      diagnostics: {
        counters: payload.diagnostics?.counters || {},
        omitted: { oversizedDetails: true },
      },
    };
  }
  return fitted;
}

export function buildUploadClientTelemetryPayload({
  uploadJobId,
  uploadJobLogId,
  clientEntryId,
  targetFolderId,
  itemId,
  leaseOwnerId,
  terminalStatus,
  terminalReason,
  picker = {},
  timings = {},
  localIndexedDb = {},
  manifest = null,
  entries = null,
} = {}) {
  const sourceEntries = entries || manifest?.entries || [];
  const aggregateCounts = {
    ...(manifest?.summary || {}),
    ...entryTypeCounts(sourceEntries),
    statuses: statusCounts(sourceEntries),
  };
  const payload = sanitizeUploadDiagnosticContext({
    clientTelemetrySchemaVersion: CLIENT_TELEMETRY_SCHEMA_VERSION,
    uploadJobId,
    uploadJobLogId,
    clientEntryId,
    targetFolderId: targetFolderId || manifest?.folderId,
    itemId,
    leaseOwnerId,
    terminalStatus,
    terminalReason,
    generatedAt: new Date().toISOString(),
    picker,
    timings,
    aggregateCounts,
    localIndexedDb,
    failedEntrySamples: failedEntrySamples(sourceEntries),
    diagnostics: summarizeBufferedEvents(uploadJobId),
  });
  return fitPayload(payload);
}

export function markUploadTelemetryFlushed(uploadJobId, terminalStatus) {
  if (!uploadJobId || !terminalStatus) {
    return false;
  }
  const key = `${uploadJobId}:${terminalStatus}`;
  if (flushedTelemetryByJob.has(key)) {
    return false;
  }
  flushedTelemetryByJob.add(key);
  return true;
}

export function uploadDiagnosticLogThrottled(level, event, throttleKey, context = {}, throttleMs = DEFAULT_THROTTLE_MS) {
  const key = `${event}:${throttleKey || "default"}`;
  const now = Date.now();
  const last = throttleState.get(key) || 0;
  if (now - last < throttleMs) {
    return false;
  }
  throttleState.set(key, now);
  uploadDiagnosticLog(level, event, context);
  return true;
}

export function resetUploadDiagnosticThrottle() {
  throttleState.clear();
  bufferedEvents.splice(0, bufferedEvents.length);
  Object.keys(eventCounters).forEach((key) => delete eventCounters[key]);
  flushedTelemetryByJob.clear();
}
